import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from 'lib/supabase';
import { useApp } from 'contexts/AppContext';
import { useLeague } from 'contexts/LeagueContext';

import NavigationBar from 'components/ui/NavigationBar';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import PlayerSelectionCard from './components/PlayerSelectionCard';
import GameOutcomeSelector from './components/GameOutcomeSelector';
import StatsTable from './components/StatsTable';
import GameSummaryPanel from './components/GameSummaryPanel';
import ConfirmSaveModal from './components/ConfirmSaveModal';
import SuccessToast from './components/SuccessToast';

const today = new Date()?.toISOString()?.split('T')?.[0];

const GameRecordingPage = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { players, playersLoading, fetchGamesWithStats, updateGame } = useApp();
  const { activeLeague, isAdmin } = useLeague();

  // Edit mode detection
  const editGame = routerLocation.state?.editGame || null;
  const isEditMode = !!editGame;

  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [playerTeams, setPlayerTeams] = useState({});
  const [gameDate, setGameDate] = useState(today);
  const [location, setLocation] = useState('');
  const [stats, setStats] = useState({});
  const [errors, setErrors] = useState({});
  const [statErrors, setStatErrors] = useState({});
  const [showDetailedStats, setShowDetailedStats] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [gameSaved, setGameSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Pre-fill form when editing
  useEffect(() => {
    if (!editGame) return;
    const allStats = [...(editGame.winners || []), ...(editGame.losers || [])];
    const existingPlayerIds = new Set(players?.map((p) => p?.id));
    setGameDate(editGame.date || today);
    setLocation(editGame.location || '');
    setSelectedPlayerIds(allStats.map((s) => s.player_id).filter((id) => existingPlayerIds.has(id)));
    const teams = {};
    const statsMap = {};
    allStats.forEach((s) => {
      teams[s.player_id] = s.team;
      statsMap[s.player_id] = {
        points: s.points ?? 0,
        rebounds: s.rebounds ?? 0,
        assists: s.assists ?? 0,
        steals: s.steals ?? 0,
        blocks: s.blocks ?? 0,
      };
    });
    setPlayerTeams(teams);
    setStats(statsMap);
    const hasDetailedStats = allStats.some((s) => (s.rebounds || 0) > 0 || (s.assists || 0) > 0 || (s.steals || 0) > 0 || (s.blocks || 0) > 0);
    if (hasDetailedStats) setShowDetailedStats(true);
  }, [editGame, players]);

  const selectedPlayers = players?.filter((p) => selectedPlayerIds?.includes(p?.id));

  const handleTogglePlayer = useCallback((playerId) => {
    setSelectedPlayerIds((prev) => {
      const isCurrentlySelected = prev?.includes(playerId);
      if (isCurrentlySelected) {
        setPlayerTeams((t) => {
          const next = { ...t };
          delete next?.[playerId];
          return next;
        });
        return prev?.filter((id) => id !== playerId);
      }
      return [...prev, playerId];
    });
  }, []);

  const handleTeamChange = useCallback((playerId, team) => {
    setPlayerTeams((prev) => ({ ...prev, [playerId]: team }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next?.teams;
      return next;
    });
  }, []);

  const handleStatChange = useCallback((playerId, field, value) => {
    const num = value === '' ? '' : parseInt(value, 10);
    setStats((prev) => ({
      ...prev,
      [playerId]: { ...prev?.[playerId], [field]: value === '' ? '' : isNaN(num) ? '' : num },
    }));
    setStatErrors((prev) => {
      const playerErrors = { ...prev?.[playerId] };
      delete playerErrors?.[field];
      return { ...prev, [playerId]: playerErrors };
    });
  }, []);

  const validate = () => {
    const newErrors = {};
    const newStatErrors = {};
    let valid = true;

    if (selectedPlayerIds?.length === 0) {
      newErrors.players = 'Please select at least one player.';
      valid = false;
    }

    if (!gameDate) {
      newErrors.gameDate = 'Please select a game date.';
      valid = false;
    }

    if (selectedPlayerIds?.length > 0) {
      const unassigned = selectedPlayerIds?.filter((id) => !playerTeams?.[id]);
      if (unassigned?.length > 0) {
        newErrors.teams = `${unassigned?.length} player${unassigned?.length !== 1 ? 's' : ''} still need a Won/Lost result assigned.`;
        valid = false;
      } else if (selectedPlayerIds?.length > 1) {
        const hasWinner = selectedPlayerIds?.some((id) => playerTeams?.[id] === 'Winners');
        const hasLoser = selectedPlayerIds?.some((id) => playerTeams?.[id] === 'Losers');
        if (!hasWinner || !hasLoser) {
          newErrors.teams = 'Please assign at least one winner and one loser.';
          valid = false;
        } else {
          const winnersTotal = selectedPlayerIds?.filter((id) => playerTeams?.[id] === 'Winners')?.reduce((sum, id) => sum + (parseInt(stats?.[id]?.points, 10) || 0), 0);
          const losersTotal = selectedPlayerIds?.filter((id) => playerTeams?.[id] === 'Losers')?.reduce((sum, id) => sum + (parseInt(stats?.[id]?.points, 10) || 0), 0);
          if ((winnersTotal > 0 || losersTotal > 0) && winnersTotal < losersTotal) {
            newErrors.teams = 'The Winners scored fewer points than the Losers. Fix team assignments or update stats.';
            valid = false;
          }
        }
      }
    }

    selectedPlayers?.forEach((player) => {
      const playerStats = stats?.[player?.id] || {};
      const fieldLimits = showDetailedStats ? { points: 100, rebounds: 30, assists: 30, steals: 15, blocks: 15 } : { points: 100 };
      Object.entries(fieldLimits)?.forEach(([field, max]) => {
        const val = playerStats?.[field];
        if (val !== '' && val !== undefined) {
          const num = parseInt(val, 10);
          if (isNaN(num) || num < 0) {
            if (!newStatErrors?.[player?.id]) newStatErrors[player.id] = {};
            newStatErrors[player.id][field] = 'Invalid';
            valid = false;
          } else if (num > max) {
            if (!newStatErrors?.[player?.id]) newStatErrors[player.id] = {};
            newStatErrors[player.id][field] = `Max ${max}`;
            valid = false;
          }
        }
      });
    });

    setErrors(newErrors);
    setStatErrors(newStatErrors);
    return valid;
  };

  const handleSaveClick = () => { if (validate()) setShowConfirmModal(true); };

  const handleConfirmSave = async () => {
    if (!supabase) { setSaveError('Database not connected. Please configure Supabase.'); setShowConfirmModal(false); return; }
    setIsSaving(true);
    setSaveError(null);
    try {
      const buildStats = (gameId) => selectedPlayers?.map((p) => ({
        game_id: gameId,
        player_id: p?.id,
        team: playerTeams?.[p?.id] || 'Unassigned',
        points: stats?.[p?.id]?.points ?? 0,
        rebounds: stats?.[p?.id]?.rebounds ?? 0,
        assists: stats?.[p?.id]?.assists ?? 0,
        steals: stats?.[p?.id]?.steals ?? 0,
        blocks: stats?.[p?.id]?.blocks ?? 0,
      }));

      if (isEditMode) {
        await updateGame(editGame.id, {
          date: gameDate,
          location: location || '',
          winning_team: 'Winners',
          notes: '',
        }, buildStats(editGame.id));

        setGameSaved(true);
        setShowSuccessToast(true);
      } else {
        const { data: gameData, error: gameError } = await supabase.from('games').insert({
          date: gameDate,
          location: location || '',
          winning_team: 'Winners',
          notes: '',
          league_id: activeLeague?.id,
        })?.select()?.single();

        if (gameError) throw gameError;

        const { error: statsError } = await supabase.from('game_stats').insert(buildStats(gameData?.id));
        if (statsError) throw statsError;

        await fetchGamesWithStats();

        setGameSaved(true);
        setShowSuccessToast(true);
      }
    } catch (err) {
      setSaveError(err?.message || 'Failed to save game. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearAll = () => {
    setSelectedPlayerIds([]);
    setPlayerTeams({});
    setGameDate(today);
    setLocation('');
    setStats({});
    setErrors({});
    setStatErrors({});
    setShowDetailedStats(false);
    setGameSaved(false);
    setSaveError(null);
    setShowConfirmModal(false);
    if (isEditMode) {
      navigate('/game-recording', { replace: true, state: {} });
    }
  };

  const winnerNames = selectedPlayers?.filter((p) => playerTeams?.[p?.id] === 'Winners')?.map((p) => p?.name);
  const loserNames = selectedPlayers?.filter((p) => playerTeams?.[p?.id] === 'Losers')?.map((p) => p?.name);

  const winnersWithStats = selectedPlayers?.filter((p) => playerTeams?.[p?.id] === 'Winners')?.map((p) => ({
    name: p?.name, points: stats?.[p?.id]?.points ?? 0, rebounds: stats?.[p?.id]?.rebounds ?? 0,
    assists: stats?.[p?.id]?.assists ?? 0, steals: stats?.[p?.id]?.steals ?? 0, blocks: stats?.[p?.id]?.blocks ?? 0,
  }));
  const losersWithStats = selectedPlayers?.filter((p) => playerTeams?.[p?.id] === 'Losers')?.map((p) => ({
    name: p?.name, points: stats?.[p?.id]?.points ?? 0, rebounds: stats?.[p?.id]?.rebounds ?? 0,
    assists: stats?.[p?.id]?.assists ?? 0, steals: stats?.[p?.id]?.steals ?? 0, blocks: stats?.[p?.id]?.blocks ?? 0,
  }));
  const winnersScore = winnersWithStats?.reduce((s, p) => s + (p?.points || 0), 0);
  const losersScore = losersWithStats?.reduce((s, p) => s + (p?.points || 0), 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <NavigationBar />
      <main className="main-content">
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
          {!isAdmin && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200">
              <Icon name="Info" size={16} color="#2563EB" />
              <span className="font-caption text-sm" style={{ color: '#2563EB' }}>You have view-only access. Only league admins can record games.</span>
            </div>
          )}
          {/* Live Game CTA */}
          {!isEditMode && (
            <button
              onClick={() => navigate('/live-game')}
              className="w-full mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border transition-all hover:shadow-md"
              style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(232,93,4,0.12)' }}>
                <Icon name="Play" size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-heading font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>Start a Live Game</p>
                <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Tap to score in real time during pickup</p>
              </div>
              <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" />
            </button>
          )}

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: 'var(--color-foreground)' }}>
                {isEditMode ? 'Edit Game' : 'Record Game'}
              </h1>
              <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
                {isEditMode ? 'Update game results and player statistics' : 'Log game results and individual player statistics'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isEditMode && (
                <Button variant="outline" size="sm" onClick={() => navigate('/history')} iconName="ArrowLeft" iconPosition="left">Back</Button>
              )}
              <Button variant="outline" size="sm" onClick={handleClearAll} iconName="RotateCcw" iconPosition="left">Clear All</Button>
              <Button variant="default" size="sm" onClick={handleSaveClick} iconName="Save" iconPosition="left" disabled={!isAdmin}>
                {isEditMode ? 'Update Game' : 'Save Game'}
              </Button>
            </div>
          </div>

          {saveError && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)' }}>
              <Icon name="AlertCircle" size={16} color="#DC2626" />
              <span className="font-caption text-sm" style={{ color: '#DC2626' }}>{saveError}</span>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0 space-y-6">
              {/* Player Selection */}
              <div className="bg-[var(--color-card)] rounded-xl border border-[var(--color-border)] p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-semibold text-base md:text-lg" style={{ color: 'var(--color-foreground)' }}>Select Players</h3>
                    <p className="font-caption text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>Choose who played, then mark each as Won or Lost</p>
                  </div>
                  <span className="font-data text-sm font-semibold px-2.5 py-1 rounded-lg" style={{ backgroundColor: 'rgba(232,93,4,0.12)', color: 'var(--color-primary)' }}>
                    {selectedPlayerIds?.length}/{players?.length}
                  </span>
                </div>

                {errors?.players && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200">
                    <Icon name="AlertCircle" size={14} color="#DC2626" />
                    <span className="font-caption text-xs text-red-600">{errors?.players}</span>
                  </div>
                )}

                {errors?.teams && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200">
                    <Icon name="AlertCircle" size={14} color="#D97706" />
                    <span className="font-caption text-xs text-amber-700">{errors?.teams}</span>
                  </div>
                )}

                {playersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
                  </div>
                ) : players?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
                    <p className="font-caption text-sm mt-2" style={{ color: 'var(--color-muted-foreground)' }}>No players yet. <button className="underline" style={{ color: 'var(--color-primary)' }} onClick={() => navigate('/players')}>Add players first</button></p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-2 md:gap-3">
                    {players?.map((player) => (
                      <PlayerSelectionCard
                        key={player?.id}
                        player={{ ...player, avatar: player?.avatar_url || '', avatarAlt: `${player?.name} player avatar`, number: player?.number || '' }}
                        isSelected={selectedPlayerIds?.includes(player?.id)}
                        onToggle={handleTogglePlayer}
                        playerTeam={playerTeams?.[player?.id]}
                        onTeamChange={handleTeamChange}
                      />
                    ))}
                  </div>
                )}

                {selectedPlayerIds?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex flex-wrap gap-1.5">
                    {selectedPlayers?.map((p) => (
                      <span
                        key={p?.id}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-caption text-xs font-medium ${
                          playerTeams?.[p?.id] === 'Winners' ?'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30'
                            : playerTeams?.[p?.id] === 'Losers' ?'bg-red-100 text-red-700 dark:bg-red-950/30' :'bg-orange-100 text-orange-700'
                        }`}
                      >
                        <Icon
                          name={
                            playerTeams?.[p?.id] === 'Winners' ?'Trophy'
                              : playerTeams?.[p?.id] === 'Losers' ?'ThumbsDown' :'HelpCircle'
                          }
                          size={10}
                          color="currentColor"
                        />
                        {p?.name}
                        <button onClick={() => handleTogglePlayer(p?.id)} className="hover:opacity-70 transition-opacity" aria-label={`Remove ${p?.name}`}>
                          <Icon name="X" size={10} color="currentColor" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Game Details */}
              <GameOutcomeSelector
                outcome={null}
                onOutcomeChange={() => {}}
                gameDate={gameDate}
                onDateChange={setGameDate}
                location={location}
                onLocationChange={setLocation}
                errors={errors}
                hideOutcome
              />

              {/* Stats Table */}
              {selectedPlayers?.length > 0 && (
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setShowDetailedStats((v) => !v)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-caption text-xs font-medium transition-colors"
                    style={{ color: 'var(--color-primary)', backgroundColor: showDetailedStats ? 'rgba(232,93,4,0.12)' : 'transparent' }}
                  >
                    <Icon name={showDetailedStats ? 'ChevronUp' : 'ChevronDown'} size={14} color="currentColor" />
                    {showDetailedStats ? 'Hide' : 'Show'} detailed stats
                  </button>
                </div>
              )}
              <StatsTable
                selectedPlayers={selectedPlayers?.map((p) => ({ ...p, avatar: p?.avatar_url || '', avatarAlt: `${p?.name} player avatar`, number: p?.number || '' }))}
                stats={stats}
                onStatChange={handleStatChange}
                errors={statErrors}
                playerTeams={playerTeams}
                showDetailedStats={showDetailedStats}
              />

              {/* Bottom Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pb-4">
                <Button variant="outline" fullWidth onClick={handleClearAll} iconName="RotateCcw" iconPosition="left">Clear All</Button>
                <Button variant="default" fullWidth onClick={handleSaveClick} iconName="Save" iconPosition="left" disabled={!isAdmin}>
                  {isEditMode ? 'Update Game' : 'Save Game'}
                </Button>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="w-full lg:w-72 xl:w-80 flex-shrink-0">
              <GameSummaryPanel
                selectedPlayers={selectedPlayers?.map((p) => ({ ...p, avatar: p?.avatar_url || '', avatarAlt: `${p?.name} player avatar` }))}
                stats={stats}
                gameDate={gameDate}
                playerTeams={playerTeams}
                showDetailedStats={showDetailedStats}
              />
            </div>
          </div>
        </div>
      </main>

      <ConfirmSaveModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmSave}
        onCancel={() => {
          setShowConfirmModal(false);
          if (gameSaved) {
            if (isEditMode) navigate('/history');
            else handleClearAll();
          }
        }}
        isSaving={isSaving}
        selectedCount={selectedPlayers?.length}
        gameDate={gameDate}
        location={location}
        winners={winnerNames}
        losers={loserNames}
        winnersWithStats={winnersWithStats}
        losersWithStats={losersWithStats}
        winnersScore={winnersScore}
        losersScore={losersScore}
        savedSuccessfully={gameSaved}
        isEditMode={isEditMode}
      />
      <SuccessToast isVisible={showSuccessToast} onClose={() => setShowSuccessToast(false)} message={isEditMode ? 'Game updated successfully!' : 'Game recorded successfully!'} />
    </div>
  );
};

export default GameRecordingPage;
